import { ParsedBrand } from '../../types/brand'

interface BrandProfileDrawerProps {
  brand: ParsedBrand | null
  isOpen: boolean
  onClose: () => void
}

function formatWebsiteLink(website: string, brandName: string) {
  if (!website || website === 'unknown') return null
  
  // Ensure URL has protocol
  const url = website.startsWith('http') ? website : `https://${website}`
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 underline font-medium"
    >
      {brandName} ↗
    </a>
  )
}

function formatCategories(categories: string[], title: string) {
  if (!categories || categories.length === 0) return null
  
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h4>
      <div className="flex flex-wrap gap-1">
        {categories.map((category, idx) => (
          <span
            key={idx}
            className="inline-block px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded"
          >
            {category}
          </span>
        ))}
      </div>
    </div>
  )
}

function getConfidenceColor(score: number) {
  if (score >= 8) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
  if (score >= 6) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
  if (score >= 4) return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20'
  return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
}

function formatManufacturingStatus(status: string) {
  const statusMap = {
    confirmed_usa: { label: 'Confirmed USA', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    likely_usa: { label: 'Likely USA', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
    mixed: { label: 'Mixed', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
    imported: { label: 'Imported', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
    unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' }
  }
  
  const mapped = statusMap[status as keyof typeof statusMap] || statusMap.unknown
  
  return (
    <span className={`inline-block px-3 py-1 text-sm rounded-full font-medium ${mapped.color}`}>
      {mapped.label}
    </span>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100 col-span-2">{value}</dd>
    </div>
  )
}

export function BrandProfileDrawer({ brand, isOpen, onClose }: BrandProfileDrawerProps) {
  if (!brand) return null

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Brand Profile
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Detailed information and analytics
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-full pb-20">
          <div className="p-6 space-y-8">
            {/* Brand Header */}
            <div className="text-center pb-6 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {brand.brand}
              </h1>
              {formatWebsiteLink(brand.website, brand.brand) && (
                <div className="mb-4">
                  {formatWebsiteLink(brand.website, brand.brand)}
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-3">
                {formatManufacturingStatus(brand.manufacturing_verification)}
                <span className={`inline-block px-3 py-1 text-sm rounded-full font-medium ${getConfidenceColor(brand.confidence_score)}`}>
                  {brand.confidence_score}/10 Confidence
                </span>
              </div>
            </div>

            {/* Consumer Summary */}
            {brand.consumer_summary && brand.consumer_summary !== 'unknown' && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  About This Brand
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {brand.consumer_summary}
                </p>
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              <dl className="space-y-0">
                <InfoRow 
                  label="Founded" 
                  value={typeof brand.founded_year === 'number' ? brand.founded_year : 'Unknown'} 
                />
                <InfoRow 
                  label="Business Type" 
                  value={brand.business_type !== 'unknown' ? brand.business_type : 'Unknown'} 
                />
                <InfoRow 
                  label="Price Tier" 
                  value={brand.price_tier.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                />
                <InfoRow 
                  label="Amazon Presence" 
                  value={brand.amazon_presence.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                />
              </dl>
            </div>

            {/* Location Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Location Information
              </h3>
              <dl className="space-y-0">
                <InfoRow 
                  label="Headquarters" 
                  value={brand.hq_location !== 'unknown' ? brand.hq_location : 'Unknown'} 
                />
                <InfoRow 
                  label="Manufacturing" 
                  value={
                    brand.mfg_locations.length > 0 
                      ? brand.mfg_locations.join(', ') 
                      : 'Unknown'
                  } 
                />
              </dl>
            </div>

            {/* Product Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Product Information
              </h3>
              <div className="space-y-4">
                {brand.primary_products && brand.primary_products !== 'unknown' && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Primary Products
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {brand.primary_products}
                    </p>
                  </div>
                )}
                
                {formatCategories(brand.clean_categories, 'Main Categories')}
                
                {brand.product_categories.length > 0 && (
                  formatCategories(brand.product_categories, 'Detailed Categories')
                )}

                {brand.purchase_methods && brand.purchase_methods !== 'unknown' && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Purchase Methods
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {brand.purchase_methods}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Certifications */}
            {brand.certifications.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Certifications & Standards
                </h3>
                {formatCategories(brand.certifications, '')}
              </div>
            )}

            {/* Data Quality & Sources */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Data Quality & Sources
              </h3>
              <dl className="space-y-0">
                <InfoRow 
                  label="Confidence Score" 
                  value={
                    <div className="flex items-center space-x-3">
                      <span className="font-medium">{brand.confidence_score}/10</span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-32">
                        <div
                          className={`h-2 rounded-full ${
                            brand.confidence_score >= 8 ? 'bg-green-500' :
                            brand.confidence_score >= 6 ? 'bg-yellow-500' :
                            brand.confidence_score >= 4 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${(brand.confidence_score / 10) * 100}%` }}
                        />
                      </div>
                    </div>
                  } 
                />
                <InfoRow 
                  label="Last Verified" 
                  value={new Date(brand.last_verified).toLocaleDateString()} 
                />
                <InfoRow 
                  label="Evidence Sources" 
                  value={brand.evidence_sources !== 'unknown' ? brand.evidence_sources : 'Not specified'} 
                />
              </dl>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This data is part of the BrandScope analytics dashboard
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}